/**
 * Custom Image Upload Modal Component
 * Reusable modal for uploading custom images to menu items/recipes
 * 
 * Features:
 * - File selection with validation
 * - Image preview with loading state
 * - Upload progress with success/error states
 * - Portal rendering to document.body
 * - Automatic cleanup on unmount
 * - Apply to variants support
 */

import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faPenToSquare, faTrash } from '@fortawesome/free-solid-svg-icons';
import UnitMappingService from '../services/unitMappingService';

const CustomImageUploadModal = ({
  isOpen = false,
  recipeId = null,
  recipeName = '',
  currentImageUrl = null,
  imageSource = null,
  onClose,
  onUploadSuccess,
  onRevertToAI,
  showCameraButton = true,
  apiUrl = process.env.REACT_APP_API_URL
}) => {
  // Modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewImageData, setPreviewImageData] = useState(null);
  const [previewImageFile, setPreviewImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  
  // Variants state
  const [showVariantsModal, setShowVariantsModal] = useState(false);
  
  // Refs
  const fileInputRef = useRef(null);

  // Cleanup on unmount or when modal closes
  useEffect(() => {
    if (!isOpen) {
      handleCleanup();
    }
  }, [isOpen]);

  // Cleanup function
  const handleCleanup = () => {
    setShowPreview(false);
    setPreviewImageData(null);
    setPreviewImageFile(null);
    setUploading(false);
    setError(null);
    setShowVariantsModal(false);
    setShowPhotoMenu(false);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle camera button click
  const handleCameraClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;


    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Only JPG, PNG, and WebP allowed');
      e.target.value = '';
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum 10MB allowed');
      e.target.value = '';
      return;
    }

    // Clear any previous errors
    setError(null);

    // Store file and show preview modal immediately
    setPreviewImageFile(file);
    setShowPreview(true);
    setPreviewImageData(null); // Will show loading state

    // Load preview in background
    const reader = new FileReader();
    reader.onload = (event) => {
      setPreviewImageData(event.target.result);
    };
    reader.onerror = () => {
      setError('Failed to read file');
      setShowPreview(false);
    };
    reader.readAsDataURL(file);

    // Reset file input for re-selection
    e.target.value = '';
  };

  // Handle upload
  const handleUpload = async () => {
    if (!previewImageFile || !recipeId) {
      setError('Missing file or recipe ID');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const businessId = UnitMappingService.getBusinessId();
      const formData = new FormData();
      formData.append('image', previewImageFile);

      const response = await fetch(`${apiUrl}/menu/upload-custom-image/${recipeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-business-id': businessId
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {

        // Notify parent component
        if (onUploadSuccess) {
          onUploadSuccess({
            imageUrl: data.imageUrl,
            imageSource: 'custom_upload',
            stats: data.stats
          });
        }

        // Check if should show variants modal
        const hasVariants = recipeName && recipeName.match(/-(half|full|single|double|family|quarter|jumbo|mini|small|medium|large|xl)$/i);
        if (hasVariants) {
          setTimeout(() => setShowVariantsModal(true), 300);
        }
        
        // Close preview modal immediately (no success overlay)
        setTimeout(() => {
          handleClosePreview();
        }, 150);
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('❌ Upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Handle revert to AI
  const handleRevert = async () => {
    try {
      const businessId = UnitMappingService.getBusinessId();

      const response = await fetch(`${apiUrl}/menu/custom-image/${recipeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        }
      });

      const data = await response.json();

      if (data.success) {
        if (onRevertToAI) {
          onRevertToAI();
        }
      } else {
        throw new Error(data.error || 'Revert failed');
      }
    } catch (err) {
      console.error('❌ Revert error:', err);
      setError(err.message || 'Failed to revert image');
    }
  };

  // Handle apply to variants
  const handleApplyToVariants = async () => {
    try {
      setShowVariantsModal(false);

      const businessId = UnitMappingService.getBusinessId();

      const response = await fetch(`${apiUrl}/menu/apply-to-variants/${recipeId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
          'x-business-id': businessId
        }
      });

      const data = await response.json();

      if (data.success && onUploadSuccess) {
        // Notify parent to refresh
        onUploadSuccess({ 
          variantsUpdated: true, 
          count: data.updatedCount 
        });
      }
    } catch (err) {
      console.error('❌ Apply to variants error:', err);
    }
  };

  // Handle close preview modal
  const handleClosePreview = () => {
    if (uploading) return;
    setShowPreview(false);
    setPreviewImageData(null);
    setPreviewImageFile(null);
    setError(null);
  };

  // Handle close main modal
  const handleCloseMain = () => {
    handleCleanup();
    if (onClose) onClose();
  };

  // Get base name for variants
  const getBaseName = () => {
    return recipeName?.replace(/\s*-\s*(half|full|single|double|family|quarter|jumbo|mini|small|medium|large|xl)$/i, '') || '';
  };

  // Render camera/pencil button overlay
  const renderCameraButton = () => {
    if (!showCameraButton || !recipeId) return null;

    const hasCustomImage = Boolean(
      currentImageUrl && 
      !currentImageUrl.includes('default.png') && 
      !currentImageUrl.includes('via.placeholder.com') && 
      !currentImageUrl.includes('Generating+Image')
    );

    return (
      <>
        {/* Camera or Pencil Overlay Button */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            if (hasCustomImage) {
              setShowPhotoMenu(prev => !prev);
            } else {
              handleCameraClick();
            }
          }}
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            backgroundColor: hasCustomImage ? '#ffffff' : 'rgba(0, 0, 0, 0.55)',
            boxShadow: hasCustomImage ? '0 2px 6px rgba(0, 0, 0, 0.2)' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            zIndex: 15,
            backdropFilter: 'blur(2px)'
          }}
          title={hasCustomImage ? "Edit or delete dish photo" : "Upload custom dish image"}
        >
          <FontAwesomeIcon
            icon={hasCustomImage ? faPenToSquare : faCamera}
            style={{ color: hasCustomImage ? '#000000' : '#ffffff', fontSize: '13px' }}
          />
        </div>

        {/* Action Dropdown Menu when photo exists */}
        {hasCustomImage && showPhotoMenu && (
          <>
            {/* Backdrop */}
            <div
              onClick={(e) => {
                e.stopPropagation();
                setShowPhotoMenu(false);
              }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999
              }}
            />
            {/* Menu */}
            <div
              style={{
                position: 'absolute',
                top: '42px',
                right: '8px',
                backgroundColor: '#ffffff',
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
                border: '1px solid #e5e7eb',
                zIndex: 1000,
                overflow: 'hidden',
                minWidth: '145px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPhotoMenu(false);
                  handleCameraClick();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '9px 12px',
                  border: 'none',
                  background: '#ffffff',
                  color: '#000000',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <FontAwesomeIcon icon={faPenToSquare} style={{ color: '#000000', fontSize: '13px' }} />
                Change Photo
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPhotoMenu(false);
                  handleRevert();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '9px 12px',
                  border: 'none',
                  background: '#ffffff',
                  color: '#000000',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%'
                }}
              >
                <FontAwesomeIcon icon={faTrash} style={{ color: '#000000', fontSize: '13px' }} />
                Delete Photo
              </button>
            </div>
          </>
        )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </>
    );
  };

  // Render preview modal
  const renderPreviewModal = () => {
    if (!showPreview) return null;

    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999999,
          isolation: 'isolate'
        }}
        onClick={handleClosePreview}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
            position: 'relative'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '20px 20px 0 20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
              Upload Custom Image
            </h2>
            <button
              onClick={handleClosePreview}
              disabled={uploading}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                color: '#000',
                lineHeight: 1,
                padding: 0,
                width: '32px',
                height: '32px'
              }}
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '20px 20px 20px 20px', position: 'relative' }}>
            {/* Preview Image */}
            {previewImageData ? (
              <img
                src={previewImageData}
                alt="Preview"
                style={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0'
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                border: '2px solid #e0e0e0',
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>⏳</div>
                  <div style={{ fontSize: '16px', color: '#666' }}>Loading preview...</div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: '#fee',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#c00'
              }}>
                ❌ {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: '0 20px 20px 20px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'space-between'
          }}>
            <button
              onClick={handleClosePreview}
              disabled={uploading}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '10px 24px',
                fontSize: '14px',
                fontWeight: '600',
                border: '1px solid #ddd',
                borderRadius: '6px',
                background: '#fff',
                cursor: uploading ? 'not-allowed' : 'pointer',
                color: '#333',
                textAlign: 'center'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleUpload}
              disabled={uploading || !previewImageData}
              style={{
                flex: 1,
                minWidth: 0,
                padding: '10px 28px',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '6px',
                background: (uploading || !previewImageData) ? '#ccc' : '#000',
                color: '#fff',
                cursor: (uploading || !previewImageData) ? 'not-allowed' : 'pointer',
                textAlign: 'center'
              }}
            >
              {uploading ? 'Updating...' : (!previewImageData ? 'Loading...' : 'Confirm')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render variants modal
  const renderVariantsModal = () => {
    if (!showVariantsModal) return null;

    const baseName = getBaseName();

    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}
        onClick={() => setShowVariantsModal(false)}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: '#fff',
            borderRadius: '12px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }}
        >
          {/* Header */}
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
              Apply to All Variants?
            </h2>
            <button
              onClick={() => setShowVariantsModal(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#999'
              }}
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '24px' }}>
            <p style={{ fontSize: '15px', lineHeight: '1.6', marginBottom: '20px' }}>
              Do you want to apply this custom image to all variants of
              <strong> "{baseName}"</strong>?
            </p>

            <div style={{
              background: '#e7f5ff',
              padding: '12px',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#0c5460',
              marginBottom: '15px'
            }}>
              📝 This will update all recipes with names like:
              <ul style={{ marginTop: '8px', marginLeft: '20px' }}>
                <li>{baseName} - Half</li>
                <li>{baseName} - Full</li>
                <li>{baseName} - Family</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowVariantsModal(false)}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: 'pointer',
                  color: '#333'
                }}
              >
                No, Just This One
              </button>
              <button
                onClick={handleApplyToVariants}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: '6px',
                  background: '#28a745',
                  color: '#fff',
                  cursor: 'pointer'
                }}
              >
                Yes, Apply to All
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <>
      {renderCameraButton()}
      {renderPreviewModal()}
      {renderVariantsModal()}
    </>
  );
};

export default CustomImageUploadModal;
